import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AjaxContext, AppContext } from 'commons-ui';
import { AxiosResponse } from 'axios';
import useService from '../../utils/useService';
import { dehydrateFormData } from '../../utils/formDataParser';
import { ICustomResponse, IDowntimeManagementConfigResponse, IFormattedDownTimeMeterPayload } from '../../utils/type';
import { DowntimeManagementConstants } from '../../utils/constant';

const useEdit = () => {
	const [showLoader, setShowLoader] = useState(false);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const history = useHistory();
	const { requestUpdateConfig } = useService();
	const [configData, setConfigData] = useState<any>({});

	const updateConfig = (downTimeConfig: IDowntimeManagementConfigResponse) => {
		setShowLoader(true);
		requestUpdateConfig(appName, { ...downTimeConfig }).then(
			(res: ICustomResponse) => {
				if (res?.status !== 200 || !res?.data?.success) {
					showAlert(res?.data?.error || 'Error occured while updating the downtime management config', { type: 'error' });
					setShowLoader(false);
				} else {
					showAlert('Config Updated');
					history.goBack();
				}
			},
			(err: any) => {
				showAlert(`Error: ${err?.response?.data?.error}`, { type: 'error' });
				setShowLoader(false);
			}
		);
	};

	const fetchDowntimeManagementConfig = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: AxiosResponse<{ success?: boolean; data?: any }> = await postData({
				endpoint: DowntimeManagementConstants.GET_CONFIG_URI,
				body: {},
				queryParams: {
					appName
				}
			});
			if (!result?.data?.success) throw new Error('Error in fetching the downtime management config!');
			const data: IDowntimeManagementConfigResponse = result?.data?.data;
			const formattedData: IFormattedDownTimeMeterPayload = {
				defaultDowntimeMeter: {
					'defaultDowntimeMeter-low': data?.defaultDowntimeMeter?.low,
					'defaultDowntimeMeter-poor': data?.defaultDowntimeMeter?.poor,
					'defaultDowntimeMeter-critical': data?.defaultDowntimeMeter?.critical
				},
				bank: {
					'bank-low': data?.bank?.low,
					'bank-poor': data?.bank?.poor,
					'bank-critical': data?.bank?.critical
				},
				instrument: {
					'instrument-low': data?.instrument?.low,
					'instrument-poor': data?.instrument?.poor,
					'instrument-critical': data?.instrument?.critical
				},
				handle: {
					'handle-low': data?.handle?.low,
					'handle-poor': data?.handle?.poor,
					'handle-critical': data?.handle?.critical
				}
			};
			setConfigData(formattedData);
			return;
		} catch (error) {
			showAlert('Error in fetching the downtime management config', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		fetchDowntimeManagementConfig();
	}, []);

	const handleFormSubmit = (submittedData: IFormattedDownTimeMeterPayload) => {
		updateConfig(dehydrateFormData(submittedData));
	};

	const handleFormCancel = () => {
		history.goBack();
	};
	return {
		configData,
		showLoader,
		handleFormSubmit,
		handleFormCancel,
		setShowLoader
	};
};

export default useEdit;
